import { datadogRum } from '@datadog/browser-rum';
import React, {
  PropsWithChildren,
  Suspense,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import styled from 'styled-components';
import ModalContext from '|/app/modals';
import Loading from '|/components/base/loading';
import ClickBlockingContext from '|/context/click-blocking-context';
import DevicePlatformContext from '|/context/device-platform-context';
import UserInitializationContext from '|/context/user-initialization-context';
import useAnalyticsContext from '|/hooks/use-analytics-context';
import useNavigateToNextStep from '|/hooks/use-navigate-to-next-step';
import { MobileWebviewEvents } from '../mobile-webview-events';
import sendNativeEvent from '../send-native-event';

const ChildrenContainer = styled.div.attrs<{ className?: string }>(props => ({
  className: props.className
}))<React.ComponentProps<'div'>>`
  height: 100%;

  &.hidden {
    display: none;
  }
`;

const defaultState = {
  load: <T extends object | null = null>(
    _: NoInfer<Promise<(T extends object ? T : null) | undefined>>,
    _signal?: AbortSignal,
    _ignoreEvent?: boolean
  ) => Promise.resolve<(T extends object ? T : null) | undefined>(undefined),
  submit: <T extends object | null = null>(
    _: NoInfer<Promise<(T extends object ? T : null) | undefined>>,
    _signal?: AbortSignal | null,
    _nextStepOnSuccess?: boolean
  ) => Promise.resolve<(T extends object ? T : null) | undefined>(undefined),
  error: null as unknown,
  isSubmitting: false,
  isLoading: false
};

/**
 * @deprecated use the FormStateContext instead
 */
const AppFrameworkContext = createContext(defaultState);

const BaseApplicationShell: React.FC<
  React.PropsWithChildren<{
    isLoading: boolean;
    loadError: unknown;
  }>
> = ({ isLoading, loadError, children }) => {
  if (loadError !== null) {
    throw loadError instanceof Error
      ? loadError
      : new Error('Unexpected error', { cause: loadError });
  }
  return (
    <Suspense fallback={<Loading />}>
      {isLoading && <Loading />}
      <ChildrenContainer className={isLoading ? 'hidden' : ''}>{children}</ChildrenContainer>
    </Suspense>
  );
};

const usePageState = () => {
  const { setShouldBlock } = useContext(ClickBlockingContext);
  const navigateToNextStep = useNavigateToNextStep();
  const [loadingCount, setLoadingCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [submitError, setSubmitError] = useState<unknown>(null);

  const load = useCallback(
    async <T extends object | null = null>(
      promise: NoInfer<Promise<(T extends object ? T : null) | undefined>>,
      signal?: AbortSignal
    ): Promise<Awaited<(T extends object ? T : null) | undefined>> => {
      setLoadingCount(count => count + 1);
      try {
        return await promise;
      } catch (errorFromLoad) {
        if (signal?.aborted) {
          return undefined;
        }
        setLoadError(errorFromLoad);
        throw errorFromLoad;
      } finally {
        setLoadingCount(count => count - 1);
      }
    },
    [setLoadingCount, setLoadError]
  );

  const submit = useCallback(
    async <T extends object | null>(
      promise: NoInfer<Promise<(T extends object ? T : null) | undefined>>,
      signal: AbortSignal | null = null,
      nextStepOnSuccess = true
    ): Promise<Awaited<(T extends object ? T : null) | undefined>> => {
      setIsSubmitting(true);
      setSubmitError(null);
      setShouldBlock(true);
      try {
        const value = await promise;
        if (nextStepOnSuccess) navigateToNextStep();
        setIsSubmitting(false);
        return value;
      } catch (errorFromSubmit) {
        if (signal?.aborted) {
          return undefined;
        }
        const errorToSend =
          errorFromSubmit instanceof Error
            ? errorFromSubmit
            : new Error('Unexpected error', { cause: errorFromSubmit });
        datadogRum.addError(errorToSend, {
          customType: 'pageSubmit'
        });
        setSubmitError(errorFromSubmit);
        setIsSubmitting(false);
        throw errorFromSubmit;
      } finally {
        setShouldBlock(false);
      }
    },
    [setIsSubmitting, setSubmitError, navigateToNextStep, setShouldBlock]
  );

  const isLoading = loadingCount > 0;

  return { load, submit, loadError, submitError, isLoading, isSubmitting };
};

const usePageStateWithEventing = (
  loadEventName: string,
  submitEventName: string,
  extraContext: Record<string, string> | null = null
) => {
  const { trackEvent } = useAnalyticsContext();
  const { accessToken } = useContext(UserInitializationContext);
  const [loadingCount, setLoadingCount] = useState(0);
  const prevLoadingCountRef = useRef(0);
  const {
    load: baseLoad,
    submit: baseSubmit,
    loadError,
    submitError,
    isLoading,
    isSubmitting
  } = usePageState();
  const devicePlatform = useContext(DevicePlatformContext);

  useEffect(() => {
    if (loadingCount === 0 && prevLoadingCountRef.current > 0) {
      sendNativeEvent({
        event: MobileWebviewEvents.PAGE_READY,
        source: 'purchase'
      });
    }
    prevLoadingCountRef.current = loadingCount;
  }, [loadingCount]);

  const url = window.location.pathname;

  const sendEvent = useCallback(
    <T extends object | null | undefined>(
      eventName: string,
      success: boolean,
      duration: number,
      data: T
    ) => {
      const ddContext = datadogRum.getInternalContext();
      const attributesToSend = {
        datadogSessionId: ddContext?.session_id,
        datadogViewId: ddContext?.view?.id
      };
      const metadata = {
        ...(data ?? {}),
        ...(extraContext ?? {}),
        ...attributesToSend
      };
      trackEvent(eventName, {
        url,
        userId: accessToken?.user.user_id,
        successEvent: success,
        duration,
        metadata,
        devicePlatform
      });
    },
    [trackEvent, url, extraContext, devicePlatform, accessToken]
  );

  const sendNoFlagEvent = useCallback(
    <T extends object | null | undefined>(eventName: string, duration: number, data: T) => {
      const ddContext = datadogRum.getInternalContext();
      const attributesToSend = {
        datadogSessionId: ddContext?.session_id,
        datadogViewId: ddContext?.view?.id
      };
      const metadata = {
        ...(data ?? {}),
        ...(extraContext ?? {}),
        ...attributesToSend
      };
      trackEvent(eventName, {
        url,
        userId: accessToken?.user.user_id,
        duration,
        metadata,
        devicePlatform
      });
    },
    [trackEvent, url, extraContext, devicePlatform, accessToken]
  );

  const sendLoadEvent = useCallback(
    <T extends object | null | undefined>(success: boolean, duration: number, data: T) => {
      sendEvent(loadEventName, success, duration, data);
    },
    [sendEvent, loadEventName]
  );

  const sendSubmitEvent = useCallback(
    <T extends object | null | undefined>(success: boolean, duration: number, data: T) => {
      sendEvent(submitEventName, success, duration, data);
    },
    [sendEvent, submitEventName]
  );

  const load = useCallback(
    async <T extends object | null>(
      promise: NoInfer<Promise<(T extends object ? T : null) | undefined>>,
      signal?: AbortSignal,
      ignoreEvent?: boolean
    ): Promise<Awaited<(T extends object ? T : null) | undefined>> => {
      const startTime = window.performance.now();
      setLoadingCount(count => count + 1);
      try {
        const data = await baseLoad<T>(promise, signal);
        const endTime = window.performance.now();
        const duration = endTime - startTime;
        if (!ignoreEvent) sendLoadEvent(true, duration, data);
        return data;
      } catch (errorFromLoad) {
        if (signal?.aborted) {
          return;
        }
        const endTime = window.performance.now();
        const duration = endTime - startTime;
        const errorToSend =
          errorFromLoad instanceof Error
            ? errorFromLoad
            : new Error('Unexpected error', { cause: errorFromLoad });
        datadogRum.addError(errorToSend, {
          customType: 'pageLoad'
        });
        sendLoadEvent(false, duration, errorToSend);
        sendNoFlagEvent(`${loadEventName}_error`, duration, errorToSend);
        throw errorFromLoad;
      } finally {
        setLoadingCount(count => count - 1);
      }
    },
    [sendLoadEvent, baseLoad, sendNoFlagEvent, loadEventName]
  );

  const submit = useCallback(
    async <T extends object | null>(
      promise: NoInfer<Promise<(T extends object ? T : null) | undefined>>,
      signal: AbortSignal | null = null,
      nextStepOnSuccess = true
    ): Promise<Awaited<(T extends object ? T : null) | undefined>> => {
      const startTime = window.performance.now();
      try {
        const value = await baseSubmit<T>(promise, signal, nextStepOnSuccess);
        const endTime = window.performance.now();
        const duration = endTime - startTime;
        sendSubmitEvent(true, duration, value);
        return value;
      } catch (errorFromSubmit) {
        if (signal?.aborted) {
          return;
        }
        const endTime = window.performance.now();
        const duration = endTime - startTime;
        const errorToSend =
          errorFromSubmit instanceof Error
            ? errorFromSubmit
            : new Error('Unexpected error', { cause: errorFromSubmit });
        datadogRum.addError(errorToSend, {
          customType: 'pageSubmit'
        });
        sendSubmitEvent(false, duration, errorToSend);
        sendNoFlagEvent(`${submitEventName}_error`, duration, errorToSend);
      }
    },
    [sendSubmitEvent, baseSubmit, submitEventName, sendNoFlagEvent]
  );

  return { load, submit, loadError, submitError, isLoading, isSubmitting };
};

/**
 * @deprecated use the FormSuspense component instead
 */
const SimpleAppFrameworkContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { load, submit, isLoading, isSubmitting, loadError, submitError } = usePageState();

  return (
    <>
      <AppFrameworkContext.Provider
        value={{ load, submit, error: submitError, isSubmitting, isLoading }}
      >
        <BaseApplicationShell isLoading={isLoading} loadError={loadError}>
          {children}
        </BaseApplicationShell>
      </AppFrameworkContext.Provider>
    </>
  );
};

/**
 * @deprecated use the FormSuspense component instead
 */
const AppFrameworkContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { load, submit, isLoading, isSubmitting, loadError, submitError } =
    usePageStateWithEventing('pp_load', 'pp_complete');

  return (
    <>
      <AppFrameworkContext.Provider
        value={{ load, submit, error: submitError, isSubmitting, isLoading }}
      >
        <BaseApplicationShell isLoading={isLoading} loadError={loadError}>
          {children}
        </BaseApplicationShell>
      </AppFrameworkContext.Provider>
    </>
  );
};

/**
 * @deprecated use the FormSuspense component instead
 */
const ModalFrameworkContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { currentModal } = useContext(ModalContext);
  const extraContext = useMemo<Record<string, string>>(() => {
    return { modalName: currentModal ?? '' };
  }, [currentModal]);
  const { load, submit, isLoading, isSubmitting, loadError, submitError } =
    usePageStateWithEventing('pp_modal_load', 'pp_modal_complete', extraContext);

  return (
    <>
      <AppFrameworkContext.Provider
        value={{ load, submit, error: submitError, isSubmitting, isLoading }}
      >
        <BaseApplicationShell isLoading={isLoading} loadError={loadError}>
          {children}
        </BaseApplicationShell>
      </AppFrameworkContext.Provider>
    </>
  );
};

export default AppFrameworkContext;
export {
  AppFrameworkContextProvider,
  ModalFrameworkContextProvider,
  SimpleAppFrameworkContextProvider
};
